// ─────────────────────────────────────────────────────────────────
// Glass-i — Other Charges Master grid columns (Stage 6OC.1 · ERP-grid aligned).
//
// Column schema for the shared <ErpDataGrid /> (same shape as
// supplierColumns / customerColumns): AntD columns + grid metadata
// (`dataType`, `sorter`, `filters`+`onFilter`, `ellipsis`, `hidden`).
//
// The grid renders its OWN ⋯ action column (Edit / Delete) and its own
// search/sort/filter — so this file contributes NO action column.
// Activate / Deactivate is wired via the page's right-click context menu.
//
// Enum codes render via the *Label helpers (enums, NOT business data).
// Multi-value cells (material / glass / thickness / category) render a
// truncated comma list; the grid's ellipsis wrapper adds the hover
// tooltip. Material-type filter options are passed in from the loaded
// master dropdowns (NEVER hardcoded). Internal rule-engine enums used
// for filters are allowed hardcoded constants.
// ─────────────────────────────────────────────────────────────────

import { Tag, Button, Tooltip } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import {
  modeLabel,
  valueByLabel,
  conditionTypeLabel,
  chargeBaseLabel,
  displayAsLabel,
  normalizeCodeList,
  formulaCodeLabel,
  sourceColumnLabel,
  uomLabel,
  quantityBasisLabel,
  showResultModeLabel,
  FORMULA_CODES,
  UOMS,
  QUANTITY_BASES,
  SHOW_RESULT_MODES,
  SOURCE_COLUMNS
} from './otherChargesHelpers';

const MUTED = <span className="erp-grid__muted">—</span>;

// Internal rule-engine enums — allowed hardcoded (NOT business data).
const MODES = ['INR', 'PERCENT', 'MULTIPLIER'];
const VALUE_BY = [
  'PER_SQM_MM', 'PER_MM', 'PER_SQM', 'PER_SQFT', 'PER_PCS',
  'PER_RFT', 'PER_RMT', 'PER_MTR', 'FIXED', 'CONDITION_BASED'
];
const CONDITION_TYPES = [
  'ALWAYS', 'ANY_SIDE_EXCEEDS', 'WIDTH_EXCEEDS', 'HEIGHT_EXCEEDS',
  'AREA_EXCEEDS', 'AREA_SLAB', 'SIZE_OR_AREA', 'SIZE_AND_AREA'
];

// Truncated comma-separated code list. ['ALL'] / empty → muted "All".
function renderCodes(value) {
  const list = normalizeCodeList(value);
  if (list.length === 0 || list.includes('ALL')) {
    return <span className="erp-grid__muted">All</span>;
  }
  return list.join(', ');
}

// Match a code-list column against a single selected filter value.
function codeListFilter(rule, field, value) {
  const list = normalizeCodeList(rule[field]);
  if (value === 'ALL') return list.length === 0 || list.includes('ALL');
  return list.includes(value);
}

function renderSlab(rule, i) {
  const area = Number(rule[`slab${i}Area`]) || 0;
  const val = Number(rule[`slab${i}Value`]) || 0;
  if (area <= 0) return MUTED;
  return <span style={{ fontSize: 12 }}>{`≥${area}→${val}`}</span>;
}

// Polish applicability cell. A rule bound to a polish (e.g. CEP) renders a
// coloured tag so it stands out from the un-restricted (ALL) rules. NULL / '' /
// 'ALL' all mean "every polish" and render as a muted "All".
function renderPolish(value) {
  const code = String(value || 'ALL').trim().toUpperCase();
  if (!code || code === 'ALL') return <span className="erp-grid__muted">All</span>;
  return <Tag color="magenta" className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>{code}</Tag>;
}
function polishFilter(rule, value) {
  const code = String((rule && rule.polishTypeCode) || 'ALL').trim().toUpperCase();
  if (value === 'ALL') return !code || code === 'ALL';
  return code === value;
}

export function buildOtherChargeColumns({ materialFilterOptions = [], polishFilterOptions = [], onMove = null, sortedIds = [] } = {}) {
  return [
    // The numeric "Order" column is intentionally hidden — the sequence is shown
    // by the row order (backend sorts by sort_order ASC) and controlled by the
    // Seq ▲/▼ buttons below.
    // Sequence reorder — move a rule up / down the execution order. Charges are
    // calculated in this order with compounding (a later charge stacks on the
    // ones before it), and the change applies only to NEW proformas.
    {
      title: 'Seq',
      key: '__seq',
      width: 70,
      align: 'center',
      fixed: 'left',
      exportable: false,
      hideable: false,
      render: (_v, record) => {
        const idx = Array.isArray(sortedIds) ? sortedIds.indexOf(record && record.id) : -1;
        const isFirst = idx <= 0;
        const isLast = idx < 0 || idx === sortedIds.length - 1;
        const stop = (e) => { if (e && e.stopPropagation) e.stopPropagation(); };
        return (
          <span style={{ display: 'inline-flex', gap: 2 }} onClick={stop}>
            <Tooltip title="Move up (earlier in sequence)">
              <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={isFirst || !onMove}
                onClick={(e) => { stop(e); if (onMove) onMove(record, 'up'); }} />
            </Tooltip>
            <Tooltip title="Move down (later in sequence)">
              <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={isLast || !onMove}
                onClick={(e) => { stop(e); if (onMove) onMove(record, 'down'); }} />
            </Tooltip>
          </span>
        );
      }
    },
    {
      // Charge Code is auto-derived from the Charge Name and hidden in the
      // form — hide it from the default grid view too (still available via
      // the grid's column show/hide toolbar).
      title: 'Code',
      key: 'chargeCode',
      dataIndex: 'chargeCode',
      width: 140,
      dataType: 'string',
      sorter: true,
      hidden: true,
      render: (v) => (v ? <code className="erp-grid__code">{v}</code> : MUTED)
    },
    {
      title: 'Charge Name',
      key: 'chargeName',
      dataIndex: 'chargeName',
      width: 210,
      dataType: 'string',
      sorter: true,
      ellipsis: true,
      render: (v) => (v ? <span className="erp-grid__primary">{v}</span> : MUTED)
    },
    {
      title: 'Applicable Material Types',
      key: 'applicableMaterialTypeCodes',
      dataIndex: 'applicableMaterialTypeCodes',
      width: 190,
      dataType: 'string',
      ellipsis: true,
      filters: materialFilterOptions,
      onFilter: (value, rule) => codeListFilter(rule, 'applicableMaterialTypeCodes', value),
      render: renderCodes
    },
    {
      title: 'Glass Types',
      key: 'glassTypeCodes',
      dataIndex: 'glassTypeCodes',
      width: 180,
      dataType: 'string',
      ellipsis: true,
      render: renderCodes
    },
    {
      title: 'Mode',
      key: 'mode',
      dataIndex: 'mode',
      width: 110,
      dataType: 'enum',
      filters: MODES.map((m) => ({ text: modeLabel(m), value: m })),
      onFilter: (value, rule) => rule.mode === value,
      render: (v) => (v ? <Tag className="erp-grid__tag">{modeLabel(v)}</Tag> : MUTED)
    },
    {
      title: 'Value By',
      key: 'valueBy',
      dataIndex: 'valueBy',
      width: 140,
      dataType: 'enum',
      filters: VALUE_BY.map((c) => ({ text: valueByLabel(c), value: c })),
      onFilter: (value, rule) => rule.valueBy === value,
      render: (v) => (v ? valueByLabel(v) : MUTED)
    },
    // ── Stage 6B.8A-3O — Formula + Area-Basis/UOM descriptors ──
    {
      title: 'Formula',
      key: 'formulaCode',
      dataIndex: 'formulaCode',
      width: 140,
      dataType: 'enum',
      filters: FORMULA_CODES.map((c) => ({ text: formulaCodeLabel(c), value: c })),
      onFilter: (value, rule) => rule.formulaCode === value,
      render: (v) => (v ? <Tag className="erp-grid__tag">{formulaCodeLabel(v)}</Tag> : MUTED)
    },
    {
      title: 'Source Column',
      key: 'sourceColumn',
      dataIndex: 'sourceColumn',
      width: 130,
      dataType: 'enum',
      filters: SOURCE_COLUMNS.map((c) => ({ text: sourceColumnLabel(c), value: c })),
      onFilter: (value, rule) => rule.sourceColumn === value,
      render: (v) => (v ? sourceColumnLabel(v) : MUTED)
    },
    {
      title: 'UOM',
      key: 'uom',
      dataIndex: 'uom',
      width: 90,
      dataType: 'enum',
      filters: UOMS.map((c) => ({ text: uomLabel(c), value: c })),
      onFilter: (value, rule) => rule.uom === value,
      render: (v) => (v ? uomLabel(v) : MUTED)
    },
    {
      title: 'Quantity Basis',
      key: 'quantityBasis',
      dataIndex: 'quantityBasis',
      width: 130,
      dataType: 'enum',
      filters: QUANTITY_BASES.map((c) => ({ text: quantityBasisLabel(c), value: c })),
      onFilter: (value, rule) => rule.quantityBasis === value,
      render: (v) => (v ? quantityBasisLabel(v) : MUTED)
    },
    {
      title: 'Show Result',
      key: 'showResultMode',
      dataIndex: 'showResultMode',
      width: 130,
      dataType: 'enum',
      filters: SHOW_RESULT_MODES.map((c) => ({ text: showResultModeLabel(c), value: c })),
      onFilter: (value, rule) => rule.showResultMode === value,
      render: (v) => (v ? showResultModeLabel(v) : MUTED)
    },
    {
      title: 'Group',
      key: 'displayGroup',
      dataIndex: 'displayGroup',
      width: 150,
      dataType: 'string',
      ellipsis: true,
      render: (v) => (v ? <span className="erp-grid__muted">{v}</span> : MUTED)
    },
    {
      title: 'Rule Value',
      key: 'ruleValue',
      dataIndex: 'ruleValue',
      width: 100,
      align: 'right',
      dataType: 'number',
      sorter: true,
      render: (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
    },
    {
      title: 'Condition Type',
      key: 'conditionType',
      dataIndex: 'conditionType',
      width: 150,
      dataType: 'enum',
      filters: CONDITION_TYPES.map((c) => ({ text: conditionTypeLabel(c), value: c })),
      onFilter: (value, rule) => rule.conditionType === value,
      render: (v) => (v ? conditionTypeLabel(v) : MUTED)
    },
    {
      title: 'Limit MM',
      key: 'dimensionLimitMm',
      dataIndex: 'dimensionLimitMm',
      width: 96,
      align: 'right',
      dataType: 'number',
      sorter: true,
      render: (v) => {
        const n = Number(v) || 0;
        return n > 0 ? n : MUTED;
      }
    },
    {
      title: 'Area Basis',
      key: 'areaBasis',
      dataIndex: 'areaBasis',
      width: 140,
      dataType: 'string',
      render: (v) => v || MUTED
    },
    {
      title: 'Area Limit',
      key: 'areaLimitSqm',
      dataIndex: 'areaLimitSqm',
      width: 100,
      align: 'right',
      dataType: 'number',
      sorter: true,
      render: (v) => {
        const n = Number(v) || 0;
        return n > 0 ? n : MUTED;
      }
    },
    {
      title: 'Charge Base',
      key: 'chargeBase',
      dataIndex: 'chargeBase',
      width: 160,
      dataType: 'string',
      render: (v) => (v ? chargeBaseLabel(v) : MUTED)
    },
    { title: 'Slab 1', key: 'slab1', width: 86, align: 'center', render: (_v, r) => renderSlab(r, 1) },
    { title: 'Slab 2', key: 'slab2', width: 86, align: 'center', render: (_v, r) => renderSlab(r, 2) },
    { title: 'Slab 3', key: 'slab3', width: 86, align: 'center', render: (_v, r) => renderSlab(r, 3) },
    {
      title: 'Display',
      key: 'displayAs',
      dataIndex: 'displayAs',
      width: 130,
      dataType: 'string',
      render: (v) => (v ? displayAsLabel(v) : MUTED)
    },
    {
      title: 'Thickness',
      key: 'thicknessCodes',
      dataIndex: 'thicknessCodes',
      width: 150,
      dataType: 'string',
      ellipsis: true,
      render: renderCodes
    },
    {
      // Stage 6OC.3 — polish applicability. A rule bound to a polish (e.g. CEP)
      // applies ONLY to lines with that polish; 'All' applies to every polish.
      title: 'Polish',
      key: 'polishTypeCode',
      dataIndex: 'polishTypeCode',
      width: 110,
      dataType: 'enum',
      filters: (Array.isArray(polishFilterOptions) ? polishFilterOptions : []),
      onFilter: (value, rule) => polishFilter(rule, value),
      render: renderPolish
    },
    {
      // categoryCodes is implemented but not part of the default 24-column
      // view — kept available via the grid's column show/hide toolbar.
      title: 'Category',
      key: 'categoryCodes',
      dataIndex: 'categoryCodes',
      width: 150,
      dataType: 'string',
      ellipsis: true,
      hidden: true,
      render: renderCodes
    },
    {
      title: 'Auto',
      key: 'autoInclude',
      dataIndex: 'autoInclude',
      width: 90,
      align: 'center',
      dataType: 'boolean',
      filters: [{ text: 'Auto', value: true }, { text: 'Manual', value: false }],
      onFilter: (value, rule) => rule.autoInclude === value,
      render: (v) => (v === true
        ? <Tag color="green" className="erp-grid__tag">Auto</Tag>
        : <Tag className="erp-grid__tag">Manual</Tag>)
    },
    {
      title: 'Taxable',
      key: 'taxable',
      dataIndex: 'taxable',
      width: 84,
      align: 'center',
      dataType: 'boolean',
      filters: [{ text: 'Yes', value: true }, { text: 'No', value: false }],
      onFilter: (value, rule) => rule.taxable === value,
      render: (v) => (v === true
        ? <Tag color="green" className="erp-grid__tag">Yes</Tag>
        : <Tag className="erp-grid__tag">No</Tag>)
    },
    {
      title: 'GST %',
      key: 'gstRate',
      dataIndex: 'gstRate',
      width: 78,
      align: 'right',
      dataType: 'number',
      sorter: true,
      render: (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? `${n}%` : MUTED;
      }
    },
    {
      title: 'HSN/SAC',
      key: 'hsnSac',
      dataIndex: 'hsnSac',
      width: 104,
      dataType: 'string',
      render: (v) => (v ? <code className="erp-grid__code">{v}</code> : MUTED)
    },
    {
      title: 'Active',
      key: 'active',
      dataIndex: 'active',
      width: 96,
      align: 'center',
      fixed: 'right',
      dataType: 'enum',
      filters: [{ text: 'Active', value: true }, { text: 'Inactive', value: false }],
      onFilter: (value, rule) => rule.active === value,
      render: (v) => (v === true
        ? <Tag color="green" className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>ACTIVE</Tag>
        : <Tag color="red" className="erp-grid__tag" style={{ marginInlineEnd: 0 }}>INACTIVE</Tag>)
    }
  ];
}

export default buildOtherChargeColumns;
